import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, WhatsappLogo, CheckCircle } from "@phosphor-icons/react";
import { Reveal, Chapter } from "./shared";
import { WHATSAPP_URL } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FORMSPREE = process.env.REACT_APP_FORMSPREE_ENDPOINT;

const inputCls =
  "w-full bg-white border border-slate-300 px-4 py-3.5 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0284C7] focus:ring-1 focus:ring-[#0284C7] transition-[border-color] duration-150";

const labelCls =
  "block font-mono text-xs font-semibold tracking-[0.2em] uppercase text-slate-500 mb-2";

export default function QuoteForm() {
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", glass_area: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(
        FORMSPREE,
        { ...form, _subject: `New free on-site demo request — ${form.business || form.name}` },
        { headers: { Accept: "application/json" } }
      );
      setSent(true);
      toast.success("Request received. We'll be in touch within one working day.");
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead, we reply fast.");
    } finally {
      setSending(false);
    }
    try {
      await axios.post(`${API}/leads`, form);
    } catch (e) {
      // best-effort database backup; Formspree already delivered the enquiry
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="bg-slate-900 py-20 md:py-32 text-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Chapter title="Book Your Free Demo" dark note="(20 MIN)" />
        <div className="grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <h2 className="font-heading font-black uppercase tracking-tight text-3xl md:text-4xl leading-tight">
              Feel it working.{" "}
              <span className="text-[#38BDF8]">Then decide.</span>
            </h2>
            <div className="mt-10 space-y-5 font-mono text-sm">
              <a
                data-testid="contact-phone-link"
                href="tel:+35679029089"
                className="flex items-center gap-4 text-slate-300 hover:text-white transition-colors duration-150"
              >
                <Phone size={20} className="text-[#38BDF8]" /> +356 7902 9089
              </a>
              <a
                data-testid="contact-email-link"
                href="mailto:contact@thermoglasspro.com"
                className="flex items-center gap-4 text-slate-300 hover:text-white transition-colors duration-150"
              >
                <EnvelopeSimple size={20} className="text-[#38BDF8]" /> contact@thermoglasspro.com
              </a>
            </div>
            <a
              data-testid="quote-whatsapp-alt-btn"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-3 bg-[#25D366] text-slate-900 font-semibold px-6 py-3.5 hover:bg-[#1eb857] hover:-translate-y-0.5 transition-[background-color,transform] duration-150"
            >
              <WhatsappLogo size={20} weight="fill" />
              Prefer WhatsApp? Message us now
            </a>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            {sent ? (
              <div
                data-testid="quote-form-success"
                className="border border-slate-700 bg-slate-800/60 p-10 md:p-14 h-full flex flex-col justify-center"
              >
                <CheckCircle size={44} weight="fill" className="text-[#25D366]" />
                <h3 className="mt-6 font-heading font-bold text-2xl md:text-3xl">
                  Request received.
                </h3>
                <p className="mt-4 text-slate-400 text-base md:text-lg leading-relaxed max-w-md">
                  Thanks. We'll contact you within one working day to schedule your
                  free on-site demo. Need it faster? We're one tap away on
                  WhatsApp.
                </p>
              </div>
            ) : (
              <form
                data-testid="quote-form"
                onSubmit={submit}
                className="border border-slate-700 bg-slate-800/60 p-8 md:p-10"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls} htmlFor="q-name">Your Name *</label>
                    <input
                      id="q-name"
                      data-testid="quote-name-input"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Maria Camilleri"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="q-business">Business or Property</label>
                    <input
                      id="q-business"
                      data-testid="quote-business-input"
                      minLength={2}
                      value={form.business}
                      onChange={set("business")}
                      placeholder="Hotel, office, block, villa name..."
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="q-email">Email *</label>
                    <input
                      id="q-email"
                      data-testid="quote-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@business.com"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="q-phone">Phone</label>
                    <input
                      id="q-phone"
                      data-testid="quote-phone-input"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+356 ..."
                      className={inputCls}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="q-area">Approx. Glass Area</label>
                    <input
                      id="q-area"
                      data-testid="quote-area-input"
                      value={form.glass_area}
                      onChange={set("glass_area")}
                      placeholder="e.g. 20 windows, one full facade, ~40 m²"
                      className={inputCls}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="q-message">Message</label>
                    <textarea
                      id="q-message"
                      data-testid="quote-message-input"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us about your space: sun exposure, floors, opening hours..."
                      className={inputCls}
                    />
                  </div>
                </div>
                <button
                  data-testid="quote-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="mt-8 w-full sm:w-auto bg-[#0284C7] text-white font-semibold px-10 py-4 border border-[#0284C7] hover:bg-[#0369A1] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 transition-[background-color,transform] duration-150"
                >
                  {sending ? "Sending..." : "Book My Free On-Site Demo"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
